import { Alert, Box, Button, DialogContent, Grid, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { IntermediatePlaceholder, Props, State } from "./interfaces";
import SinglePlaceholderPrefill from "./SinglePlaceholderPrefill";
import { Container, DateContainer, DocumentViewerContainer, StyledDialog } from "./styles";
import { DocumentTemplateWithValuesDTO } from "../../../../models/document-templates/document-template-version/document-template-with-values";
import {
  DocumentTemplatePrefilledPlaceholders,
  WordTemplatePlaceholdersFilter,
} from "../../../../models/document-templates/word-template-placeholders-filter";
import { DocumentFileRef } from "../../../../models/documents/document-files";
import DocumentViewer from "../../../../reusable-components/DocumentViewer/DocumentViewer";
import { fillTemplates } from "../../../../services/document-templates/word-template-api.service";
import { downloadBlob, handleDownloadFile } from "../../../../services/documents";
import { DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES } from "../../../../utils/constants/document-template-placeholder-field-types";
import { convertBase64ToBlob } from "../../../../utils/files-utils/convert-file";
import { getFileExtension } from "../../../../utils/files-utils/get-file-extension";
import { intlFormatDateFullMonth } from "../../../../utils/time-utils";
import { toastifyResponse } from "../../../../utils/toastify-response/toastify-response";
import ConfirmationChangesDialog from "../../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../DialogActions/DialogActions";
import DialogTitle from "../../../DialogTitle/DialogTitle";
import LoadingOverlay from "../../../LoadingOverlay/LoadingOverlay";
import StyledDate from "../../../StyledDate/StyledDate";
const PrefillDocumentTemplateToGenerate = ({
  placeholders,
  template,
  handleNotifyParent,
  children,
  version,
  versionComment,
  documentTemplateName,
  versionDate,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.document_templates.prefill_and_generate_document_from_template";
  const theme = useTheme();

  const [state, setState] = useState<State>({
    open: false,
    placeholders: [],
    loading: false,
    error: "",
    CCDOpen: false,
  });

  //#region initiate placeholders
  const initiatePlaceholders = useCallback(() => {
    const initPlaceholders = placeholders.map((originalPlaceholder) => ({
      originalPlaceholder,
      prefilledPlaceholder: {
        id: originalPlaceholder.id,
        name: originalPlaceholder.name,
        value: "",
        isInTable: originalPlaceholder.isInTable,
        index: originalPlaceholder.index,
      },
    }));

    setState((state) => ({
      ...state,
      placeholders: [...initPlaceholders],
    }));
  }, [placeholders]);
  //#endregion

  //#region update placeholders structure
  const updatePlaceholdersStructure = useCallback((): DocumentTemplatePrefilledPlaceholders => {
    const bodyPlaceholders = state.placeholders
      .filter((_placeholder) => !_placeholder.originalPlaceholder.isInTable)
      .map((_placeholder) => ({
        ..._placeholder.prefilledPlaceholder,
        value:
          _placeholder.originalPlaceholder.fieldType === DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES.Date.id
            ? intlFormatDateFullMonth(new Date(_placeholder.prefilledPlaceholder.value))
            : _placeholder.prefilledPlaceholder.value,
      }));

    const allTableIndexes = [
      ...new Set(
        state.placeholders
          .filter(
            (_placeholder) =>
              _placeholder.originalPlaceholder.isInTable && _placeholder.originalPlaceholder.tableIndex !== null
          )
          .map((_placeholder) => _placeholder.originalPlaceholder.tableIndex)
      ),
    ];

    const tablePlaceholders = allTableIndexes.map((tableIndex) => ({
      [`table${tableIndex}`]: [
        [
          ...state.placeholders
            .filter((_placeholder) => _placeholder.originalPlaceholder.tableIndex === tableIndex)
            .map((_placeholder) => ({
              ..._placeholder.prefilledPlaceholder,
              value:
                _placeholder.originalPlaceholder.fieldType === DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES.Date.id
                  ? intlFormatDateFullMonth(new Date(_placeholder.prefilledPlaceholder.value))
                  : _placeholder.prefilledPlaceholder.value,
            })),
        ],
      ],
    }));
    return {
      body: [bodyPlaceholders],
      ...Object.assign({}, ...tablePlaceholders),
    } as DocumentTemplatePrefilledPlaceholders;
  }, [state.placeholders]);
  //#endregion

  useEffect(() => {
    state.open && initiatePlaceholders();
  }, [state.open, placeholders]);

  //#region update placeholders
  const updatePlaceholders = useCallback(
    (placeholders: IntermediatePlaceholder[]) => {
      const updatedPlaceholdersIds = placeholders.map((_placeholder) => _placeholder.originalPlaceholder.id);

      setState((state) => ({
        ...state,
        placeholders: [
          ...state.placeholders.map((_placeholder) =>
            updatedPlaceholdersIds.includes(_placeholder.originalPlaceholder.id)
              ? placeholders.find(
                  (__placeholder) => __placeholder.originalPlaceholder.id === _placeholder.originalPlaceholder.id
                )!
              : _placeholder
          ),
        ],
      }));
    },
    [state.placeholders]
  );
  //#endregion

  //#region handle donwload prefilled doc
  const handleDownloadDocsFile = useCallback(async () => {
    const structuredPlaceholders = updatePlaceholdersStructure();

    if (structuredPlaceholders && template) {
      const confirmed = window.confirm(
        `${t(`components.upload_document.download_document_files_confirmation_message`)}`
      );

      if (confirmed) {
        try {
          const propsFilter: WordTemplatePlaceholdersFilter = {
            ids: [template.id],
            placeHolders: structuredPlaceholders as any,
          };

          const response = await fillTemplates(propsFilter, undefined);

          if (!response.error && response.items && response.items.length > 0) {
            const documentItem: DocumentTemplateWithValuesDTO = response.items[0];

            const fileContentBlob = convertBase64ToBlob(documentItem.content, documentItem.type);

            if (fileContentBlob) {
              // Get proper file extension and create filename
              const fileExtension = getFileExtension(template.name);
              const templateNameWithoutExt = template.name.replace(/\.[^/.]+$/, "");
              const fileName = `${templateNameWithoutExt}_filled.${fileExtension}`;

              downloadBlob(fileContentBlob, fileName);
              toastifyResponse(response);
              handleNotifyParent();
            } else {
              toast(`${t(`${tRP}.error_converting`)}`);
            }
          } else {
            toast(response.error || `${t(`${tRP}.error_filling_template`)}`);
          }
        } catch (error) {
          toast(error as string);
          console.error(`${t(`${tRP}.error_downloading`)}`, error);
        }
      }
    } else {
      await handleDownloadFile(template as DocumentFileRef);
    }
  }, [state.placeholders, template]); //#endregion

  return (
    <>
      {children ? (
        <Grid
          onClick={(e) => {
            e.stopPropagation();
            setState((state) => ({ ...state, open: true }));
          }}
          style={{ cursor: "pointer" }}
        >
          {children}
        </Grid>
      ) : (
        <Button onClick={() => setState((state) => ({ ...state, open: true }))}>click</Button>
      )}
      <StyledDialog
        open={state.open}
        fullWidth
        // maxWidth="xl"
      >
        <DialogTitle
          title={`${t(`${tRP}.title`)}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            {state.placeholders.length > 0 ? (
              <>
                <Box
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    padding: 2,
                    borderRadius: "5px",
                    marginBottom: "10px",
                  }}
                >
                  <DateContainer container>
                    <Grid item>
                      <Typography color={theme.palette.text.primary}>{documentTemplateName}</Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        {t("common_words.version")}: {version}
                      </Typography>
                      {versionComment && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          {versionComment}
                        </Typography>
                      )}
                    </Grid>
                    {versionDate && (
                      <StyledDate
                        date={new Date(versionDate).getDate()}
                        month={new Date(versionDate).getMonth()}
                        year={new Date(versionDate).getFullYear()}
                      />
                    )}
                  </DateContainer>
                </Box>
                <Grid style={{ display: "flex" }}>
                  <DocumentViewerContainer>
                    <DocumentViewer
                      fileDto={template}
                      embedded
                      defaultMainScale={0.8}
                      hideHeader
                    />
                  </DocumentViewerContainer>
                  <Container>
                    <Alert severity="info">{`${t(`${tRP}.hint`)}`} </Alert>
                    {state.placeholders
                      .sort((a, b) => a.originalPlaceholder.index - b.originalPlaceholder.index)
                      // .slice(
                      //   0,
                      //   state.placeholders.length % 2 > 0
                      //     ? (state.placeholders.length + 1) / 2
                      //     : state.placeholders.length / 2
                      // )
                      .map((_placeholder, idx) => (
                        <SinglePlaceholderPrefill
                          key={idx}
                          placeholder={_placeholder}
                          handleUpdatePlaceholders={updatePlaceholders}
                          dependentPlaceholders={state.placeholders.filter(
                            (__placeholder) =>
                              __placeholder.originalPlaceholder.relatedToPlaceholder?.id ===
                              _placeholder.originalPlaceholder.id
                          )}
                        />
                      ))}
                  </Container>
                  {/* <Container>
                    {state.placeholders
                      .sort((a, b) => a.originalPlaceholder.index - b.originalPlaceholder.index)
                      .slice(
                        state.placeholders.length % 2 > 0
                          ? (state.placeholders.length + 1) / 2
                          : state.placeholders.length / 2
                      )
                      .map((_placeholder, idx) => (
                        <SinglePlaceholderPrefill
                          key={idx}
                          placeholder={_placeholder}
                          handleUpdatePlaceholders={updatePlaceholders}
                          dependentPlaceholders={state.placeholders.filter(
                            (__placeholder) =>
                              __placeholder.originalPlaceholder.relatedToPlaceholder?.id ===
                              _placeholder.originalPlaceholder.id
                          )}
                        />
                      ))}
                  </Container> */}
                </Grid>
              </>
            ) : (
              <>{`${t(`${tRP}.no_placeholders`)}`}</>
            )}
          </LoadingOverlay>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={handleDownloadDocsFile}
          customSaveName={`${t(`${tRP}.generate_document`)}`}
        />
      </StyledDialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => {
          setState((state) => ({
            open: false,
            placeholders: [],
            loading: false,
            error: "",
            CCDOpen: false,
          }));
        }}
      />
    </>
  );
};

export default PrefillDocumentTemplateToGenerate;
